import React from "react";
import { Button, CircularProgress, Tooltip } from "@material-ui/core";
import CreditCardOutlined from "@material-ui/icons/CreditCardOutlined";
import AccountBalanceOutlined from "@material-ui/icons/AccountBalanceOutlined";
import SwapHoriz from "@material-ui/icons/SwapHoriz";
import ListAlt from "@material-ui/icons/ListAlt";
import EventOutlined from "@material-ui/icons/EventOutlined";
import LockOutlined from "@material-ui/icons/LockOutlined";
import PersonOutline from "@material-ui/icons/PersonOutline";
import LocationCityOutlined from "@material-ui/icons/LocationCityOutlined";
import FlagOutlined from "@material-ui/icons/FlagOutlined";
import DraftsOutlined from "@material-ui/icons/DraftsOutlined";
import HelpOutline from "@material-ui/icons/HelpOutline";
import { Cart as LegacyCart } from "../steps/Cart";
import ZipTax from "../../../api/ZipTax";
import {
  AddressFields,
  Field,
  STATES,
  Section,
  StepHeader,
  withUpperState
} from "./ui";
import { formatMoney } from "./values";

// ZipTax answers a ZIP code it does not know with no rate (rCode 104 or 110,
// or 100 without results). Such orders are charged zero tax. Any other answer
// or a failed request is an error the merchant can retry.
const UNKNOWN_ZIP_CODES = [104, 110];

export function taxRateFromZipTax(response) {
  if (!response) return null;
  if (response.rCode === 100 && response.results?.length)
    return (response.results[0].taxSales * 100).toFixed(2);
  if (
    (response.rCode === 100 && Array.isArray(response.results)) ||
    UNKNOWN_ZIP_CODES.includes(response.rCode)
  )
    return "0.00";
  return null;
}

// Reuse the original Cart's calculation methods. Only its presentation and
// tax loading are replaced; the old step's behavior is unchanged.
export default class Payment extends LegacyCart {
  constructor(props) {
    super(props);
    const setState = this.setState.bind(this);
    this.live = false;
    this.setState = (update, callback) => {
      if (!this.live) return;
      setState(update, callback);
    };
    this.componentDidMount = () => {
      this.live = true;
      this.loadPricing();
    };
  }

  // The same payload the legacy Cart publishes after a valid tax response.
  getPricing = taxes => {
    const { link } = this.props.application;
    return {
      subtotal: parseFloat(this.getSubtotal()),
      subtotalWithDiscount: parseFloat(this.getSubtotalWithDiscount()),
      discount:
        parseFloat(this.getSubtotal()) -
        parseFloat(this.getSubtotalWithDiscount()),
      taxes: parseFloat(this.getTaxes()),
      taxRate: parseFloat(taxes),
      orderTotal: parseFloat(this.getOrderTotal()),
      downPayment: parseFloat(this.getDownPayment()),
      balance: parseFloat(this.getBalance()),
      shipping: parseFloat(link?.shipping || 0),
      additionalFee: parseFloat(link?.additionalFee || 0)
    };
  };

  loadPricing = async () => {
    if (this.props.application.withoutPayment) return;
    this.setState({ taxError: false, loading: true });
    this.props.onPaymentLoading(true);
    let taxes = null;
    try {
      taxes = taxRateFromZipTax(
        await ZipTax.getTaxes(this.props.application.businessZip)
      );
    } catch (error) {
      taxes = null;
    }
    // Leaving Payment while loading must not publish a stale price.
    if (!this.live) return;
    if (taxes === null) {
      this.setState({ loading: false, taxError: true });
    } else {
      await new Promise(resolve =>
        this.setState({ taxes, loading: false }, resolve)
      );
      this.props.setApplicationPricing(this.getPricing(taxes));
    }
    this.props.onPaymentLoading(taxes === null);
  };

  componentWillUnmount() {
    this.live = false;
  }

  render() {
    const { application, errors, onApplicationTextChange } = this.props;
    const t = this.context;
    if (application.withoutPayment)
      return (
        <StepHeader title="Payment Info">
          Payment information is not required for this application.
        </StepHeader>
      );
    const props = { application, errors, onApplicationTextChange };
    const link = application.link;
    const ach = link && link.paymentType === "ACH";
    return (
      <>
        <StepHeader title="Payment Information" icon={LockOutlined}>
          Please review the items in your cart and add your payment information
          to proceed.
        </StepHeader>
        <Section title="Payment Information">
          <div className="merchant-grid">
            {ach ? (
              <>
                <div className="merchant-full">
                  <Field
                    {...props}
                    name="paymentStatementName"
                    label="Name on Bank Statement"
                    required
                    icon={PersonOutline}
                  />
                </div>
                <div className="merchant-full">
                  <Field
                    {...props}
                    name="paymentBankName"
                    label="Bank Name"
                    required
                    icon={AccountBalanceOutlined}
                  />
                </div>
                <Field
                  {...props}
                  name="paymentAccountNumber"
                  label="Bank Account Number"
                  required
                  icon={CreditCardOutlined}
                  secret
                />
                <Field
                  {...props}
                  name="paymentRoutingNumber"
                  label="Routing Number"
                  required
                  icon={SwapHoriz}
                  inputProps={{ inputMode: "numeric", maxLength: 9 }}
                />
                <div className="merchant-full">
                  <Field
                    {...props}
                    name="paymentAccountType"
                    label="Account Type"
                    icon={ListAlt}
                    options={["Checking", "Savings", "General Ledger"]}
                  />
                </div>
                <Field
                  {...props}
                  name="branchCity"
                  label="City"
                  required
                  icon={LocationCityOutlined}
                />
                <Field
                  {...props}
                  application={withUpperState(application, "branchState")}
                  name="branchState"
                  label="State"
                  required
                  icon={FlagOutlined}
                  options={STATES}
                />
                <Field
                  {...props}
                  name="branchZip"
                  label="Zip Code"
                  required
                  icon={DraftsOutlined}
                  inputProps={{ inputMode: "numeric", maxLength: 5 }}
                />
              </>
            ) : (
              <>
                <Field
                  {...props}
                  name="creditCardNumber"
                  label="Card Number"
                  required
                  icon={CreditCardOutlined}
                  inputProps={{ inputMode: "numeric" }}
                  autoComplete="cc-number"
                />
                <Field
                  {...props}
                  name="creditCardType"
                  label="Card Type"
                  required
                  icon={ListAlt}
                  options={[
                    { value: "visa", label: "Visa" },
                    { value: "mastercard", label: "MasterCard" },
                    { value: "amex", label: "AMEX" },
                    { value: "discover", label: "Discover" }
                  ]}
                />
                <Field
                  {...props}
                  name="creditCardExpireDate"
                  label="Expiration Date"
                  required
                  placeholder="MM/YY"
                  icon={EventOutlined}
                  autoComplete="cc-exp"
                  inputProps={{ inputMode: "numeric" }}
                />
                <Field
                  {...props}
                  name="creditCardCvv"
                  label="Card Security Code (CVV)"
                  required
                  icon={LockOutlined}
                  InputProps={{
                    endAdornment: (
                      <Tooltip
                        title={t(
                          "The 3-digit code on the back of your card. AMEX cards have a 4-digit code on the front."
                        )}
                        enterTouchDelay={0}
                      >
                        <span
                          className="merchant-help-icon"
                          tabIndex={0}
                          role="img"
                          aria-label={t("What is CVV?")}
                        >
                          <HelpOutline aria-hidden="true" />
                        </span>
                      </Tooltip>
                    )
                  }}
                  autoComplete="cc-csc"
                  inputProps={{ inputMode: "numeric", maxLength: 4 }}
                />
                <div className="merchant-full">
                  <Field
                    {...props}
                    name="billingName"
                    label="Billing Name"
                    required
                    icon={PersonOutline}
                    autoComplete="cc-name"
                  />
                </div>
                <div className="merchant-full">
                  <AddressFields {...props} prefix="billing" />
                </div>
              </>
            )}
          </div>
        </Section>
        <Section title="Order Summary">
          {this.state.loading && (
            <CircularProgress
              size={24}
              aria-label={t("Loading order summary")}
            />
          )}
          {this.state.taxError && (
            <div role="alert" className="merchant-error">
              {t("We could not calculate the order total. Please try again.")}{" "}
              <Button onClick={this.loadPricing}>{t("Try again")}</Button>
            </div>
          )}
          <table className="merchant-summary">
            <thead>
              <tr>
                <th>{t("Products")}</th>
                <th>{t("Amount")}</th>
              </tr>
            </thead>
            <tbody>
              {(link?.equipment || []).map((item, index) => (
                <tr key={item.id || index}>
                  <td>
                    {item.name}
                    <small>
                      {t("Quantity:")} {item.quantity}
                    </small>
                  </td>
                  <td>{formatMoney(item.price * item.quantity)}</td>
                </tr>
              ))}
              <tr>
                <td>{t("Discount")}</td>
                <td>
                  (
                  {formatMoney(
                    this.getSubtotal() - this.getSubtotalWithDiscount()
                  )}
                  )
                </td>
              </tr>
              <tr>
                <td>{t("Subtotal")}</td>
                <td>{formatMoney(this.getSubtotalWithDiscount())}</td>
              </tr>
              <tr>
                <td>{t("Taxes")}</td>
                <td>{formatMoney(this.getTaxes())}</td>
              </tr>
              <tr>
                <td>{t("Shipping")}</td>
                <td>{formatMoney(link?.shipping || 0)}</td>
              </tr>
              {!!link?.additionalFee && (
                <tr>
                  <td>{link.additionalFeeLabel}</td>
                  <td>{formatMoney(link.additionalFee)}</td>
                </tr>
              )}
              <tr>
                <td>
                  {t("Order Total")}
                  <small>
                    {link?.paymentType && t(link.paymentType)}
                    {link?.paymentPlan &&
                      ` · ${t("{count} Month Term", {
                        count: link.paymentPlan
                      })}`}
                  </small>
                </td>
                <td>{formatMoney(this.getOrderTotal())}</td>
              </tr>
              <tr>
                <td>{t("Down Payment")}</td>
                <td>({formatMoney(this.getDownPayment())})</td>
              </tr>
              <tr>
                <td>
                  <strong>{t("Balance Due")}</strong>
                </td>
                <td>
                  <strong>{formatMoney(this.getBalance())}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </Section>
      </>
    );
  }
}
